import React, { useEffect, useState } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import InstallMobileOutlinedIcon from '@mui/icons-material/InstallMobileOutlined';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import { ACCENT_SOFT, BORDER, Card, GradientButton, INK, MUTED, TextButton } from '../ui';

// EV-88: веб-нетворкинг мероприятия как мобильное приложение (PWA).
// Манифест и иконки отдаёт сервер для каждого мероприятия отдельно; service worker не используем.

const pwaUrl = token => `/api/p/networking/${encodeURIComponent(token)}`;

// beforeinstallprompt может прийти раньше, чем смонтируется кнопка, — запоминаем его сразу
let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach(l => l());
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', e => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

// iPadOS представляется как Mac, отличаем по тач-экрану
const isIos = () =>
  /iPad|iPhone|iPod/.test(window.navigator.userAgent) ||
  (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

/** Служебные теги в <head>: по ним телефон понимает, что страницу можно установить как приложение. */
export const usePwaHead = (info, token) => {
  useEffect(() => {
    if (!info || !info.pwaEnabled) return undefined;
    const tags = [
      ['link', { rel: 'manifest', href: `${pwaUrl(token)}/manifest.webmanifest` }],
      ['link', { rel: 'apple-touch-icon', href: `${pwaUrl(token)}/icon/180.png` }],
      ['meta', { name: 'apple-mobile-web-app-title', content: info.pwaName || '' }],
      ['meta', { name: 'apple-mobile-web-app-capable', content: 'yes' }],
      ['meta', { name: 'mobile-web-app-capable', content: 'yes' }],
      ['meta', { name: 'theme-color', content: '#ffffff' }],
    ].map(([tag, attrs]) => {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      return el;
    });
    return () => tags.forEach(el => el.remove());
  }, [info, token]);
};

/** Как можно установить: 'prompt' — системный диалог (Android/Chrome), 'ios' — по инструкции, null — никак. */
const useInstallMode = enabled => {
  const [, rerender] = useState(0);
  useEffect(() => {
    const l = () => rerender(x => x + 1);
    listeners.add(l);
    return () => listeners.delete(l);
  }, []);
  if (!enabled || isStandalone()) return null;
  if (deferredPrompt) return 'prompt';
  if (isIos()) return 'ios';
  return null;
};

const IosHint = ({ open, onClose, name }) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth='xs' PaperProps={{ sx: { borderRadius: '14px', m: 2 } }}>
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: INK, mb: 2 }}>
        Добавьте «{name}» на экран «Домой»
      </Typography>
      {[
        [IosShareIcon, <>Нажмите «Поделиться» в панели браузера</>],
        [AddBoxOutlinedIcon, <>Выберите «На экран „Домой“»</>],
        [null, <>Нажмите «Добавить» — иконка появится на экране телефона</>],
      ].map(([Icon, text], i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              background: ACCENT_SOFT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontWeight: 700,
              color: INK,
            }}
          >
            {Icon ? <Icon sx={{ fontSize: 20, color: INK }} /> : i + 1}
          </Box>
          <Typography sx={{ fontSize: 15, color: INK }}>{text}</Typography>
        </Box>
      ))}
      <Typography sx={{ fontSize: 13, color: MUTED, mb: 2 }}>
        При первом запуске с иконки войдите по почте ещё раз.
      </Typography>
      <GradientButton fullWidth onClick={onClose}>
        Понятно
      </GradientButton>
    </Box>
  </Dialog>
);

/**
 * Кнопка «Установить приложение».
 * variant='prominent' — заметная карточка на первом экране (до входа),
 * variant='compact' — скромная иконка в шапке после входа.
 */
const InstallApp = ({ info, token, variant = 'compact', sx }) => {
  const mode = useInstallMode(info && info.pwaEnabled);
  const [hintOpen, setHintOpen] = useState(false);
  if (!mode) return null;

  const install = () => {
    if (mode === 'ios') {
      setHintOpen(true);
      return;
    }
    const e = deferredPrompt;
    deferredPrompt = null;
    e.prompt();
    // если отказались, Chrome пришлёт beforeinstallprompt заново — кнопка вернётся
    e.userChoice.finally(notify);
  };

  const hint = <IosHint open={hintOpen} onClose={() => setHintOpen(false)} name={info.pwaName} />;

  if (variant === 'prominent') {
    return (
      <Card sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, ...sx }}>
        <Box
          component='img'
          src={`${pwaUrl(token)}/icon/180.png`}
          alt=''
          sx={{ width: 48, height: 48, borderRadius: '12px', border: `1px solid ${BORDER}`, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: INK }}>Приложение на телефон</Typography>
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            Установите приложение, чтобы пользоваться было удобнее
          </Typography>
        </Box>
        <GradientButton size='small' onClick={install} sx={{ flexShrink: 0 }}>
          Установить
        </GradientButton>
        {hint}
      </Card>
    );
  }

  return (
    <>
      <IconButton
        onClick={install}
        aria-label='Установить приложение'
        title='Установить приложение'
        sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: MUTED, ...sx }}
      >
        <InstallMobileOutlinedIcon fontSize='small' />
      </IconButton>
      <TextButton
        size='small'
        onClick={install}
        sx={{ display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0, ...sx }}
      >
        Установить приложение
      </TextButton>
      {hint}
    </>
  );
};

export default InstallApp;
